<!--
Copyright 2024 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license, please see LICENSE.md in the
project root for license information or contact permission@sei.cmu.edu for full terms.
-->

<div class="cssLayoutRowStartCenter">
  <div class="sp-icon">
    <mat-icon class="mdi-24px" fontIcon="mdi-book-open-outline" [ngStyle]="{'color': 'var(--mat-sys-primary)'}"></mat-icon>
  </div>
  <mat-form-field style="width: 300px; margin-left: 10px;">
    <input matInput [formControl]="filterControl" placeholder="Search" />
    @if (filterString) {
    <button mat-icon-button matSuffix (click)="filterControl.setValue('')" title="Clear Search">
      <mat-icon style="transform: scale(0.85)" fontIcon="mdi-close-circle-outline"></mat-icon>
    </button>
    }
  </mat-form-field>
  <div class="button-end">
    <mat-paginator #paginator [pageIndex]="0" [pageSize]="20"
      [pageSizeOptions]="[5, 10, 15, 20, 25, 50, 100, 200]"></mat-paginator>
  </div>
</div>

<section class="scrolling-region">
  <table mat-table #catalogTable [dataSource]="catalogDataSource" matSort multiTemplateDataRows>
    <!-- action column -->
    <ng-container matColumnDef="action">
      <mat-header-cell *matHeaderCellDef class="column-action">
        @if (canEdit) {
        <span>
          <button mat-icon-button (click)="addOrEditCatalog(null)" style="outline: none;"
            title="Add new Catalog">
            <mat-icon class="mdi-24px self-center" fontIcon="mdi-plus-circle"></mat-icon>
          </button>
          <button mat-icon-button style="outline: none;" title="Upload a new catalog from a file"
            (click)="uploadFile('json', '', ''); jsonInput.click()">
            <mat-icon class="mdi-24px" fontIcon="mdi-upload"></mat-icon>
          </button>
        </span>
        } &nbsp;
      </mat-header-cell>
      <mat-cell *matCellDef="let element" class="column-action">
        <div>
          <button mat-icon-button (click)="addOrEditCatalog(element); $event.stopPropagation();"
            title="Edit {{ element.name }} catalog" [disabled]="!canEdit">
            <mat-icon class="mdi-18px" fontIcon="mdi-square-edit-outline"
              title="Edit {{ element.name }} catalog"></mat-icon>
          </button>
          <button mat-icon-button (click)="copyCatalog(element); $event.stopPropagation();"
            title="Copy {{ element.name }}" [disabled]="!canEdit">
            <mat-icon class="mdi-18px" fontIcon="mdi-content-copy" alt="Copy"></mat-icon>
          </button>
          <button mat-icon-button title="Download {{ element.name }}"
            (click)="downloadJsonFile(element); $event.stopPropagation();" [disabled]="!canEdit">
            <mat-icon class="mdi-18px" fontIcon="mdi-download" alt="Download catalog"></mat-icon>
          </button>
          <button mat-icon-button (click)="deleteCatalog(element); $event.stopPropagation();"
            title="Delete {{ element.name }} catalog" [disabled]="!canEdit">
            <mat-icon class="mdi-18px" fontIcon="mdi-trash-can-outline"
              title="Delete {{ element.name }} catalog"></mat-icon>
          </button>
        </div>
      </mat-cell>
    </ng-container>
    <!-- ispublic Column -->
    <ng-container matColumnDef="ispublic">
      <mat-header-cell title="Is Public" *matHeaderCellDef class="column-is-public">
        Public
      </mat-header-cell>
      <mat-cell *matCellDef="let element" class="column-is-public">
        <mat-checkbox [(ngModel)]="element.isPublic" (ngModelChange)="saveCatalog(element)"
          (click)="$event.stopPropagation();" [disabled]="!canEdit"></mat-checkbox>
      </mat-cell>
    </ng-container>
    <!-- name Column -->
    <ng-container matColumnDef="name">
      <mat-header-cell title="Catalog Name" *matHeaderCellDef class="column-name">
        Name
      </mat-header-cell>
      <mat-cell *matCellDef="let element" class="column-name">
        {{ element.name }}
      </mat-cell>
    </ng-container>
    <!-- inject type Column -->
    <ng-container matColumnDef="injecttype">
      <mat-header-cell title="Inject Type" *matHeaderCellDef class="column-inject-type">
        Inject Type
      </mat-header-cell>
      <mat-cell *matCellDef="let element" class="column-inject-type">
        {{ getInjectTypeName(element.injectTypeId) }}
      </mat-cell>
    </ng-container>
    <!-- description Column -->
    <ng-container matColumnDef="description">
      <mat-header-cell title="Catalog Description" *matHeaderCellDef class="column-description">
        Description
      </mat-header-cell>
      <mat-cell *matCellDef="let element" class="column-description">
        {{ element.description }}
      </mat-cell>
    </ng-container>
    <!-- Expanded Content Column - The detail row is made up of this one column that spans across all columns -->
    <ng-container matColumnDef="expandedDetail">
      <mat-cell class="expanded-detail-cell" *matCellDef="let element" [attr.colspan]="displayedColumns.length">
        <div [@detailExpand]="element.id == expandedElementId ? 'expanded' : 'collapsed'" style="overflow: hidden">
          <mat-expansion-panel class="detail-expansion-panel">
            <mat-expansion-panel-header>Injects</mat-expansion-panel-header>
            <div class="inject-list-div">
              <app-inject-list [canEdit]="canEdit" [loggedInUserId]="loggedInUserId" [catalog]="element" [injectType]="{}"
                [isEditMode]="true"></app-inject-list>
            </div>
          </mat-expansion-panel>
          <mat-expansion-panel class="detail-expansion-panel">
            <mat-expansion-panel-header>Units with access</mat-expansion-panel-header>
            <div class="unit-list-div">
              <app-catalog-units [canEdit]="canEdit" [loggedInUserId]="loggedInUserId"
                [catalog]="element"></app-catalog-units>
            </div>
          </mat-expansion-panel>
        </div>
      </mat-cell>
    </ng-container>
    <!-- row definitions -->
    <mat-header-row *matHeaderRowDef="displayedColumns"></mat-header-row>
    <mat-row *matRowDef="let row; columns: displayedColumns;" matRipple [class]="getRowClass(row.id)"
      (click)="rowClicked(row)">
    </mat-row>
    <mat-row *matRowDef="let row; columns: ['expandedDetail']" class="detail-row">
    </mat-row>
  </table>
  <!-- No Results Message -->
  @if (catalogDataSource?.filteredData.length === 0) {
  <div class="no-results">No Catalogs found</div>
  }
</section>

<input hidden (change)="selectFile($event)" #jsonInput type="file" accept=".json" />
